import { Fragment, useState } from 'react';
import { ArrowLeft, CheckCircle2, ChevronDown, ChevronRight, Clock, RotateCw, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Badge } from '@shared/components/ui/shadcn/badge';
import { Button } from '@shared/components/ui/shadcn/button';
import { Card } from '@shared/components/ui/shadcn/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { WebhookDelivery, WebhookEndpoint, WebhookEventType, WebhookAttempt } from '../../models/Webhooks';

function DeliveryStatusIcon({ status }: { status: WebhookDelivery['status'] }) {
  if (status === 'success') {
    return <CheckCircle2 className="w-4 h-4 text-green-500" />;
  }
  if (status === 'failed') {
    return <XCircle className="w-4 h-4 text-red-500" />;
  }
  return <Clock className="w-4 h-4 text-yellow-500" />;
}

const HttpStatusBadge = ({ status }: { status: number | null | undefined }) => {
  if (!status) return <Badge variant="secondary">N/A</Badge>;

  const isSuccess = status >= 200 && status < 300;

  return (
    <Badge variant={isSuccess ? 'default' : 'destructive'} className="font-mono">
      {status}
    </Badge>
  );
};

interface AttemptItemProps {
  attempt: WebhookAttempt;
  index: number;
}

const AttemptItem = ({ attempt, index }: AttemptItemProps) => {
  const formattedDate = attempt.attempted_at
    ? format(new Date(attempt.attempted_at), 'dd MMM yyyy, HH:mm:ss', { locale: es })
    : 'N/A';

  return (
    <div className="bg-background border rounded-md p-3 text-xs font-mono shadow-sm">
      <div className="flex justify-between items-center mb-3 text-slate-500 border-b pb-2">
        <span className="font-semibold text-slate-700">Intento #{index + 1}</span>
        <span className="text-[10px]">{formattedDate}</span>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <span className="text-slate-400 block text-[10px] uppercase tracking-wider mb-1">Status</span>
          <HttpStatusBadge status={attempt.http_status} />
        </div>
        <div>
          <span className="text-slate-400 block text-[10px] uppercase tracking-wider mb-1">Duración</span>
          <span className="font-medium text-slate-700">{attempt.duration_ms ? `${attempt.duration_ms} ms` : '-'}</span>
        </div>
      </div>

      {attempt.error_message && (
        <div className="mb-3 p-2 bg-red-50 text-red-600 rounded border border-red-100 break-words font-sans">
          <span className="block text-[10px] font-bold uppercase mb-1">Error</span>
          {attempt.error_message}
        </div>
      )}

      {attempt.response_body && (
        <div>
          <span className="text-slate-400 block text-[10px] uppercase tracking-wider mb-1">Response Body</span>
          <pre className="p-2 rounded overflow-x-auto text-[11px] border max-h-[150px]">
            {typeof attempt.response_body === 'object'
              ? JSON.stringify(attempt.response_body, null, 2)
              : attempt.response_body}
          </pre>
        </div>
      )}
    </div>
  );
};

interface DeliveriesTableProps {
  endpoint: WebhookEndpoint;
  deliveries: WebhookDelivery[];
  events: WebhookEventType[];
  onBack: () => void;
  onRetry: (deliveryId: number) => void;
  isRetrying?: boolean;
}

export const DeliveriesTable = ({
  endpoint,
  deliveries,
  events,
  onBack,
  onRetry,
  isRetrying,
}: DeliveriesTableProps) => {
  const { t } = useTranslation('webhooksModule');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const getEventName = (eventCode: string) => events.find((e) => e.code === eventCode)?.description || eventCode;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('deliveries.back')}
        </Button>
        <div className="flex-1">
          <h2 className="font-semibold text-lg">{t('deliveries.title')}</h2>
          <div className="flex items-center gap-2 mt-1">
            <code className="text-xs bg-muted px-2 py-1 rounded font-mono text-muted-foreground truncate max-w-md">
              {endpoint.url}
            </code>
            {endpoint.description && (
              <span className="text-xs text-muted-foreground italic">- {endpoint.description}</span>
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground">{t('deliveries.count_other', { count: deliveries.length })}</p>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12" />
              <TableHead>{t('deliveries.columns.status')}</TableHead>
              <TableHead>{t('deliveries.columns.event')}</TableHead>
              <TableHead>{t('deliveries.columns.date')}</TableHead>
              <TableHead>{t('deliveries.columns.http')}</TableHead>
              <TableHead>{t('deliveries.columns.attempts')}</TableHead>
              <TableHead className="w-32 text-right">{t('deliveries.columns.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {deliveries.map((delivery) => {
              const isExpanded = expandedId === delivery.id;
              const formattedDate = (() => {
                if (!delivery.last_attempt_at) return '-';
                const date = new Date(delivery.last_attempt_at);
                return isNaN(date.getTime()) ? '-' : format(date, 'dd MMM, HH:mm', { locale: es });
              })();

              return (
                <Fragment key={delivery.id}>
                  <TableRow
                    className={`cursor-pointer transition-colors ${isExpanded ? 'bg-muted/40' : 'hover:bg-muted/50'}`}
                    onClick={() => setExpandedId(isExpanded ? null : delivery.id)}
                  >
                    <TableCell>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <DeliveryStatusIcon status={delivery.status} />
                        <span className="text-sm font-medium capitalize">
                          {t(`deliveries.status.${delivery.status}`)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{getEventName(delivery.event_type)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{formattedDate}</span>
                    </TableCell>
                    <TableCell>
                      <HttpStatusBadge status={delivery.last_http_status} />
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-mono">{delivery.attempt_count}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      {delivery.status === 'failed' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          disabled={isRetrying}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRetry(delivery.id);
                          }}
                        >
                          <RotateCw className={`mr-1 w-3 h-3 ${isRetrying ? 'animate-spin' : ''}`} />
                          {t('deliveries.retry')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>

                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted/10 p-0 border-b-0">
                        <div className="p-6 space-y-6 animate-in slide-in-from-top-2 duration-200">
                          {/* 1. Payload Enviado */}
                          <div className="space-y-2">
                            <h5 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                              {t('deliveries.payload')}
                            </h5>
                            <div className="border rounded-md bg-background overflow-hidden">
                              <pre className="p-4 text-xs font-mono overflow-x-auto max-h-[300px] custom-scrollbar">
                                {JSON.stringify(delivery.payload, null, 2)}
                              </pre>
                            </div>
                          </div>

                          {delivery.attempts && delivery.attempts.length > 0 && (
                            <div className="space-y-3">
                              <h5 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                                {t('deliveries.attemptsHistory')}
                              </h5>
                              <div className="grid gap-3">
                                {delivery.attempts.map((attempt, index) => (
                                  <AttemptItem
                                    key={index} // Idealmente usar attempt.id si existe
                                    attempt={attempt}
                                    index={index}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {delivery.last_response_body && (
                            <div className="space-y-2 pt-2 border-t">
                              <h5 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                                {t('deliveries.last_response_raw')}
                              </h5>
                              <pre className="bg-slate-50 border rounded-md p-3 text-xs font-mono overflow-x-auto max-h-[150px]">
                                {delivery.last_response_body}
                              </pre>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>

        {deliveries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Clock className="w-12 h-12 mb-4 opacity-20" />
            <p>{t('deliveries.empty')}</p>
          </div>
        )}
      </Card>
    </div>
  );
};
